import { useCallback } from 'react'
import { useCompaniesClient } from '@frontend/clients/companies.request'
import { useFacilitiesClient } from '@frontend/clients/facility.request'
import { useDebounce } from '@frontend/components/hooks'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { FormLocation } from '@shared/const'
import { settingsListToMap } from '@shared/lib/settings-helpers'

import { BadgeFilterParams, DriverFilterParams, FormFilterParams } from '@shared-types/company'
import { ListCompanyAuditEventTypesQuery } from '@shared-types/company'
import {
  CompanyListFacilitiesFilterParam,
  CompanyListFilterParam,
  CompanyListRampsFilterParams,
} from '@shared-types/domain'
import { CompanySettingsMap } from '@shared-types/settings/index'
import { Identifier, Nullable } from '@shared-types/util'

export function useClearFacilityRampsCache() {
  const queryClient = useQueryClient()

  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['ramps'] })
  }, [queryClient])
}

export function useClearCompanyAuditEventsCache() {
  const queryClient = useQueryClient()
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['company_audit_events'] })
  }, [queryClient])
}

export function useQueryCompanies(params: CompanyListFilterParam) {
  const companiesClient = useCompaniesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies_list', debouncedFilters],

    queryFn: async () => {
      return companiesClient.list(debouncedFilters)
    },
  })
}

export function useQueryCompanyFacilities(
  companyId?: Nullable<Identifier>,
  params?: CompanyListFacilitiesFilterParam
) {
  const facilitiesClient = useFacilitiesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['facilities', companyId, debouncedFilters],

    queryFn: async () => {
      if (!companyId) {
        return {
          results: [],
        }
      }
      return facilitiesClient.list(companyId, debouncedFilters)
    },
  })
}

export function useQueryAllCompanyFacilities(companyId?: Nullable<Identifier>) {
  const facilitiesClient = useFacilitiesClient()

  return useQuery({
    queryKey: ['facilities', companyId, 'all'],

    queryFn: async () => {
      if (!companyId) {
        return []
      }
      return facilitiesClient.listAll(companyId)
    },
  })
}

export function useQueryCompany(companyId?: Nullable<Identifier>) {
  const companiesClient = useCompaniesClient()

  return useQuery({
    queryKey: ['companies', companyId],

    queryFn: async () => {
      if (!companyId) {
        return null
      }
      return companiesClient.get(companyId)
    },
  })
}

export function useQueryCompanySettings(companyId?: Nullable<Identifier>) {
  const companiesClient = useCompaniesClient()

  return useQuery({
    queryKey: ['companies', companyId, 'settings'],

    queryFn: async () => {
      if (!companyId) {
        return null
      }
      const rawSettings = await companiesClient.getSettings(companyId)

      if (rawSettings.results) {
        return settingsListToMap<CompanySettingsMap>(rawSettings.results)
      }

      return null
    },
  })
}

export function useQueryCompanyDrivers({
  companyId,
  params,
}: {
  companyId?: Nullable<Identifier>
  params: DriverFilterParams
}) {
  const companiesClient = useCompaniesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'drivers', debouncedFilters],

    queryFn: async () => {
      if (!companyId) {
        return {
          results: [],
        }
      }
      return companiesClient.listDrivers(companyId, debouncedFilters)
    },
  })
}

export function useQueryFacilityRamps(
  companyId: Nullable<Identifier>,
  facilityId?: Nullable<Identifier>
) {
  const facilitiesClient = useFacilitiesClient()

  return useQuery({
    queryKey: ['ramps', companyId, facilityId],

    queryFn: async () => {
      if (!companyId || !facilityId) {
        return []
      }
      return facilitiesClient.getRamps(companyId, facilityId)
    },
  })
}

export function useQueryAllCompanyBadges(companyId?: Identifier, facilityId?: string) {
  const companiesClient = useCompaniesClient()

  return useQuery({
    queryKey: ['companies', companyId, 'badges', facilityId],

    queryFn: async () => {
      if (!companyId) {
        return []
      }

      return companiesClient.listAllBadges(companyId, facilityId)
    },
  })
}

export function useQueryCompanyBadges(companyId: Identifier, params: BadgeFilterParams) {
  const companiesClient = useCompaniesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'badges', debouncedFilters],

    queryFn: async () => {
      return companiesClient.listBadges(companyId, debouncedFilters)
    },
  })
}

export function useQueryCompanyForms(companyId: Identifier, params: FormFilterParams) {
  const companiesClient = useCompaniesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'forms', debouncedFilters],

    queryFn: async () => {
      return companiesClient.listForms(companyId, debouncedFilters)
    },
  })
}

export function useQueryCompanyForm(companyId: Identifier, formId?: string) {
  const companiesClient = useCompaniesClient()

  return useQuery({
    queryKey: ['companies', companyId, 'forms', formId],

    queryFn: async () => {
      if (!formId) {
        return null
      }

      return companiesClient.getForm(companyId, formId)
    },
  })
}

export function useQueryCompanyRamps(companyId: Identifier, params?: CompanyListRampsFilterParams) {
  const companiesClient = useCompaniesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['companies', companyId, 'ramps', debouncedFilters],

    queryFn: async () => {
      return companiesClient.listRamps(companyId, debouncedFilters)
    },
  })
}

export function useQueryCompanyFormByLocation({
  companyId,
  formLocation,
}: {
  companyId?: Identifier
  formLocation: FormLocation
}) {
  const companiesClient = useCompaniesClient()

  return useQuery({
    queryKey: ['companies', companyId, 'forms', formLocation],

    queryFn: async () => {
      if (!companyId) {
        return null
      }

      return companiesClient.getFormByLocation(companyId, formLocation)
    },

    // Prevent retry and refetching on window focus
    // this is to avoid unnecessary requests when the form is not available
    retry: false,
  })
}

export function useQueryListCompanyAuditEvents(
  companyId: Identifier,
  params: ListCompanyAuditEventTypesQuery
) {
  const companiesClient = useCompaniesClient()
  const debouncedFilters = useDebounce(params)

  return useQuery({
    queryKey: ['company_audit_events', companyId, debouncedFilters],

    queryFn: async () => {
      return companiesClient.listCompanyAuditEvents(companyId, debouncedFilters)
    },
  })
}

export function useQueryCompanyLogoURL(companyId: Identifier, options?: { enabled?: boolean }) {
  const companiesClient = useCompaniesClient()
  const enabled = options?.enabled ?? true

  return useQuery({
    queryKey: ['companies', companyId, 'logo'],
    queryFn: async () => {
      return companiesClient.getLogoURL(companyId)
    },
    enabled,
  })
}
